<template>
  <article class="task-group">
    <header>
      <span v-if="task.iconUrl" class="icon">
        <EncryptedImg
          :url="task.iconUrl"
          :key-hex="task.iconKey"
          :alt="task.title"
          :width="34"
          :height="34"
          fit="contain"
        />
      </span>
      <div class="titles">
        <h3>{{ task.title }}</h3>
        <p>{{ task.rule }}</p>
      </div>
      <span v-if="task.tag" class="tag">{{ task.tag }}</span>
    </header>

    <div class="track">
      <div class="fill">
        <i :style="{ width: `${percent}%` }"></i>
      </div>
      <div class="progress-info">
        <span
          >{{ currentLabel }}
          <b>{{ fmt(task.currentBet) }}</b>
        </span>
        <span v-if="isCompleted" class="muted">{{ t('promo.daily.completed') }}</span>
        <span v-else class="muted"
          >{{ t('promo.daily.nextStage') }} {{ fmt(task.nextTarget) }}</span
        >
      </div>
    </div>

    <div class="tc-stages">
      <DailyLevelCard
        v-for="level in task.levels"
        :key="level.label"
        :level="level"
        :metric="task.metric"
        :task-type="task.type"
        @action="emit('action', level)"
      />
    </div>
  </article>
</template>

<script setup lang="ts">
import { formatBaseAmt } from '~/utils/transfer/money'
import type { DailyTask, DailyTaskLevel } from '../types'
import { TASK_TYPE_META } from '../taskTypeMeta'
import DailyLevelCard from './DailyLevelCard.vue'

const props = defineProps<{ task: DailyTask }>()
const emit = defineEmits<{ action: [level: DailyTaskLevel] }>()

const { t } = useI18n()
const userStore = useUserStore()
const unit = computed(() => {
  const key = props.task.type && TASK_TYPE_META[props.task.type].unitKey
  return key ? t(key) : ''
})

const fmt = (n: number) => {
  if (props.task.metric === 'count') {
    return `${n.toLocaleString()}${unit.value ? ` ${unit.value}` : ''}`
  }
  return formatBaseAmt(n, userStore.baseCurrency)
}

const percent = computed(() => {
  const pct = props.task.progressPct
  if (pct != null) return Math.min(100, Math.max(0, Math.round(pct)))
  if (!props.task.nextTarget) return 0
  return Math.min(100, Math.round((props.task.currentBet / props.task.nextTarget) * 100))
})

const isCompleted = computed(() => percent.value >= 100)

const currentLabel = computed(() => {
  if (props.task.type) {
    return t('promo.daily.currentOf', { name: t(TASK_TYPE_META[props.task.type].labelKey) })
  }
  if (props.task.metric === 'deposit') return t('promo.daily.currentDeposit')
  if (props.task.metric === 'count') return t('promo.daily.currentCount')
  return t('promo.daily.currentBet')
})
</script>

<style scoped lang="scss">
.task-group {
  margin: 0 14px 12px;
  padding: 14px;
  border-radius: var(--r-lg);
  background: var(--surface);
  border: 1px solid var(--hairline);
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 15px;
  gap: 10px;
}

.icon {
  width: 34px;
  height: 34px;
  border-radius: var(--r-sm);
  display: grid;
  place-items: center;
  font-size: 18px;
  background: var(--surface2);
  flex: 0 0 auto;
  overflow: hidden;

  :deep(.encrypted-img-root) {
    width: 100%;
    height: 100%;
  }
}

.titles {
  flex: 1;
  min-width: 0;
}

.titles h3 {
  font-size: 14px;
  font-weight: bold;
}

.titles p {
  display: block;
  font-size: 11px;
  color: var(--muted);
  margin-top: 2px;
  font-weight: 500;
}

.tag {
  font-size: 10px;
  font-weight: 800;
  padding: 3px 9px;
  border-radius: 8px;
  background: var(--surface2);
  color: var(--muted);
}

.track {
  margin: 0 0 12px;
}

.fill {
  height: 7px;
  border-radius: var(--r-xs);
  background: var(--surface2);
  overflow: hidden;
}

.fill i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--primary), #ec4899);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  margin-top: 6px;
}

.progress-info b {
  font-weight: bold;
}

.progress-info .muted {
  color: var(--muted);
}

.tc-stages {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 2px;
  justify-content: safe center;
}
</style>
